import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import { Check, ChevronDown, Info, X } from "lucide-react";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { DIMENSION_LABELS } from "../reportBuilder.utils";
/**
 * Breakdown Dimension picker, matching the approved design.
 *
 * The trigger shows the picked dimensions as numbered chips — the number is the
 * order they were picked in. The menu is one checkbox list, pick any. Edits are
 * made on a draft and only land on Done; clicking away discards them. A chip's
 * × removes it straight away.
 */
/**
 * Not offered in the list (no age data is synced yet). The label stays above so a
 * report saved earlier still shows its chip. Keep in step with AI_HIDDEN_DIMENSIONS
 * in the backend's aiReportBuilder.service.ts.
 */
const HIDDEN_DIMENSION_IDS = ["age"];
const DIMENSION_IDS = Object.keys(DIMENSION_LABELS).filter((id) => !HIDDEN_DIMENSION_IDS.includes(id));
/** Time grains get the blue chip, breakdowns the pink one. */
const TIME_IDS = ["day", "week", "month"];
/** Short notes shown behind an info icon beside an option. */
const OPTION_INFO = {
    week: "Weeks run Monday to Sunday.",
};
const PINK = "#FF008E";
const labelOf = (id) => DIMENSION_LABELS[id] || id;
const Chip = ({ index, label, isTime, disabled, onRemove }) => {
    const color = isTime ? "#3538CD" : "#C11574";
    return (_jsxs(Box, { sx: {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            pl: 0.75,
            pr: disabled ? 0.75 : 0.5,
            py: 0.25,
            borderRadius: "6px",
            bgcolor: isTime ? "#EEF4FF" : "#FDF2FA",
            color,
            whiteSpace: "nowrap",
        }, children: [_jsx(Typography, { component: "span", sx: { fontSize: "11px !important", opacity: 0.7 }, children: index }), _jsx(Typography, { component: "span", sx: { fontSize: "13px !important", fontWeight: 600 }, children: label }), !disabled && (_jsx(Box, { role: "button", "aria-label": `Remove ${label}`, tabIndex: 0, onClick: (e) => {
                    e.stopPropagation();
                    onRemove();
                }, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        onRemove();
                    }
                }, sx: { display: "flex", cursor: "pointer", color, "&:hover": { opacity: 0.7 } }, children: _jsx(X, { size: 12, strokeWidth: 2.5 }) }))] }));
};
const OptionRow = ({ id, label, selected, onSelect, radio = false }) => (_jsxs(Box, { role: radio ? "radio" : "checkbox", "aria-checked": selected, tabIndex: 0, onClick: onSelect, onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
        }
    }, sx: {
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1.25,
        cursor: "pointer",
        bgcolor: selected ? "#FDF2F8" : "transparent",
        "&:hover": { bgcolor: selected ? "#FCE7F3" : "#F9FAFB" },
    }, children: [_jsx(Box, { sx: {
                width: 18,
                height: 18,
                borderRadius: radio ? "50%" : "4px",
                border: `1.5px solid ${selected ? PINK : "#98A2B3"}`,
                bgcolor: selected && !radio ? PINK : "#fff",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
            }, children: selected &&
                (radio ? (_jsx(Box, { sx: { width: 8, height: 8, borderRadius: "50%", bgcolor: PINK } })) : (_jsx(Check, { size: 12, strokeWidth: 3 }))) }), _jsx(Typography, { className: "all-Font-Sizes", sx: { color: selected ? "#C11574" : "#344054", fontWeight: selected ? 600 : 400 }, children: label }), OPTION_INFO[id] && (_jsx(HoverTooltip, { title: OPTION_INFO[id], placement: "top", children: _jsx(Box, { onClick: (e) => e.stopPropagation(), sx: { display: "flex", color: "#667085" }, children: _jsx(Info, { size: 14 }) }) }))] }));
const BreakdownDimensionPicker = ({ value, onChange, disabled = false, placeholder = "Select dimensions", helperText, options, heading = "Breakdown by", }) => {
    const items = options ?? DIMENSION_IDS.map((id) => ({ value: id, label: DIMENSION_LABELS[id] }));
    const itemLabel = (id) => items.find((item) => item.value === id)?.label || labelOf(id);
    // Blue "time" chips only make sense for the dimension list itself.
    const isTimeChip = (id) => !options && TIME_IDS.includes(id);
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState([]);
    const selected = value;
    const openMenu = () => {
        if (disabled)
            return;
        setDraft(value);
        setOpen(true);
    };
    const toggle = (id) => setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    // Time grain is pick-one: choosing Week replaces Day. Clicking the picked
    // one again clears it.
    const pickTime = (id) => setDraft((prev) => {
        const rest = prev.filter((x) => !TIME_IDS.includes(x));
        return prev.includes(id) ? rest : [...rest, id];
    });
    const sectionHeading = (title, hint, first = false) => (_jsxs(Box, { sx: { display: "flex", alignItems: "baseline", gap: 0.75, px: 2, pt: first ? 1.25 : 1, pb: 0.5, ...(first ? {} : { borderTop: "1px solid #F2F4F7", mt: 0.5 }) }, children: [_jsx(Typography, { sx: {
                    fontSize: "12px !important",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#667085",
                }, children: title }), _jsx(Typography, { sx: { fontSize: "11px !important", color: "#98A2B3" }, children: hint })] }));
    // The dimension list itself splits in two; a custom `options` list stays flat.
    const timeItems = options ? [] : items.filter((item) => TIME_IDS.includes(item.value));
    const breakdownItems = options ? items : items.filter((item) => !TIME_IDS.includes(item.value));
    // The dimension list needs at least one pick; a custom `options` list is optional.
    const required = !options;
    const canRemove = !required || selected.length > 1;
    const canDone = !required || draft.length > 0;
    const handleDone = () => {
        if (!canDone)
            return;
        onChange(draft);
        setOpen(false);
    };
    return (_jsxs(Box, { children: [_jsxs(Box, { ref: anchorRef, role: "button", "aria-haspopup": "dialog", "aria-expanded": open, tabIndex: disabled ? -1 : 0, onClick: openMenu, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openMenu();
                    }
                }, sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 40,
                    px: 1,
                    py: 0.625,
                    border: `1px solid ${open ? PINK : "#D0D5DD"}`,
                    borderRadius: "8px",
                    bgcolor: disabled ? "#F9FAFB" : "#fff",
                    cursor: disabled ? "not-allowed" : "pointer",
                    boxShadow: open ? "0 0 0 3px rgba(255,0,142,0.12)" : "none",
                }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", gap: 0.75 }, children: selected.length === 0 ? (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#98A2B3", px: 0.5 }, children: placeholder })) : (selected.map((id, index) => (_jsx(Chip, { index: index + 1, label: itemLabel(id), isTime: isTimeChip(id), disabled: disabled || !canRemove, onRemove: () => onChange(selected.filter((x) => x !== id)) }, id)))) }), _jsx(Box, { sx: {
                            display: "flex",
                            color: open ? PINK : "#98A2B3",
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform .15s ease",
                        }, children: _jsx(ChevronDown, { size: 16 }) })] }), helperText && (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#667085", mt: 0.75 }, children: helperText })), _jsxs(Popover, { open: open, anchorEl: anchorRef.current, onClose: () => setOpen(false), anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, slotProps: {
                    paper: {
                        sx: {
                            mt: 0.75,
                            width: anchorRef.current?.offsetWidth,
                            borderRadius: "12px",
                            border: "1px solid #EAECF0",
                            boxShadow: "0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
                            overflow: "hidden",
                        },
                    },
                }, children: [_jsxs(Box, { sx: { maxHeight: 360, overflowY: "auto" }, children: [timeItems.length > 0 && (_jsxs(_Fragment, { children: [sectionHeading("Time breakdown", "Pick any one", true), timeItems.map((item) => (_jsx(OptionRow, { id: item.value, label: item.label, radio: true, selected: draft.includes(item.value), onSelect: () => pickTime(item.value) }, item.value)))] })), sectionHeading(heading, options ? "" : "Pick any", timeItems.length === 0), breakdownItems.map((item) => (_jsx(OptionRow, { id: item.value, label: item.label, selected: draft.includes(item.value), onSelect: () => toggle(item.value) }, item.value)))] }), _jsxs(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.25,
                            mt: 0.5,
                            borderTop: "1px solid #EAECF0",
                        }, children: [_jsx(Typography, { role: "button", tabIndex: 0, onClick: () => setDraft([]), onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ")
                                        setDraft([]);
                                }, className: "all-Font-Sizes", sx: { color: "#475467", fontWeight: 500, cursor: "pointer", "&:hover": { color: "#101828" } }, children: "Clear" }), !canDone && (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#B42318", mx: 1 }, children: "Pick at least one" })), _jsx(Box, { role: "button", "aria-disabled": !canDone, tabIndex: canDone ? 0 : -1, onClick: handleDone, onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ")
                                        handleDone();
                                }, sx: {
                                    px: 2,
                                    py: 0.875,
                                    borderRadius: "8px",
                                    bgcolor: canDone ? "#101828" : "#D0D5DD",
                                    color: "#fff",
                                    fontSize: "14px !important",
                                    fontWeight: 600,
                                    cursor: canDone ? "pointer" : "not-allowed",
                                    "&:hover": { bgcolor: canDone ? "#1D2939" : "#D0D5DD" },
                                }, children: "Done" })] })] })] }));
};
export default BreakdownDimensionPicker;
